import ClearIcon from '@mui/icons-material/Clear';
import { Box, Button, Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';

import { CvSearchSyncCountersDto, CvSearchSyncStatusResponseDto } from '@/api/generated';
import { cvSearchSyncStatusStyles as styles } from '@/layouts/private/topBar/components/cvSearchSyncStatus/CvSearchSyncStatus.styles.ts';
import { CvSearchSyncStatusDrawerProblem } from '@/layouts/private/topBar/components/cvSearchSyncStatus/CvSearchSyncStatusDrawerProblem.comp.tsx';
import { useRecomputeCvSearchSyncStatus } from '@/layouts/private/topBar/components/cvSearchSyncStatus/utils/useRecomputeCvSearchSyncStatus.util.ts';
import { formatDateTime } from '@/utils/formatDate.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type Props = {
  onClose: () => void;
  open: boolean;
  syncStatus?: CvSearchSyncStatusResponseDto;
};

const COUNTERS: Array<keyof CvSearchSyncCountersDto> = [
  'orangeCandidates',
  'eligibleCandidates',
  'inCvSearch',
  'readyInCvSearch',
  'pendingSync',
  'waitingForEmbedding',
  'taggingInProgress',
  'skipped',
];

export const CvSearchSyncStatusDrawer: React.FC<Props> = ({ onClose, open, syncStatus }) => {
  const { t } = useTranslationWithPrefix('layouts.cvSearchSyncStatus');
  const { isRecomputing, recompute } = useRecomputeCvSearchSyncStatus();

  const pendingText = () => {
    if (!syncStatus?.oldestPendingSince) {
      return t('drawer.noPending');
    }
    return t('drawer.oldestPending', { time: formatDateTime(syncStatus.oldestPendingSince) });
  };

  return (
    <Drawer anchor="right" onClose={onClose} open={open}>
      <Box data-testid="cv-search-sync-status-drawer" sx={styles.drawer}>
        <Box sx={styles.header}>
          <Typography component="h2" sx={{ flexGrow: 1 }} variant="f20Medium">
            {t('drawer.title')}
          </Typography>
          <Button disabled={isRecomputing} onClick={recompute} variant="outlined">
            {t('drawer.recompute')}
          </Button>
          <Tooltip title={t('drawer.close')}>
            <IconButton aria-label={t('drawer.close')} onClick={onClose} size="small">
              <ClearIcon />
            </IconButton>
          </Tooltip>
        </Box>
        {syncStatus?.computedAt ? (
          <>
            <Stack spacing={1}>
              <Typography variant="f14Medium">
                {t('drawer.computedAt', { time: formatDateTime(syncStatus.computedAt) })}
              </Typography>
              <Typography variant="f14Regular">
                {t(syncStatus.syncEnabled ? 'drawer.syncEnabled' : 'drawer.syncDisabled')}
              </Typography>
              <Typography variant="f14Regular">{pendingText()}</Typography>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="f16Medium">{t('counters.title')}</Typography>
              <Box sx={styles.countersGrid}>
                {COUNTERS.map(counter => (
                  <Box key={counter} sx={styles.counter}>
                    <Typography variant="f12Regular">{t(`counters.${counter}`)}</Typography>
                    <Typography variant="f20Medium">
                      {syncStatus.counters?.[counter] ?? 0}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="f16Medium">{t('problems.title')}</Typography>
              <Box>
                {syncStatus.problems.map(problem => (
                  <CvSearchSyncStatusDrawerProblem
                    key={problem.category}
                    onNavigate={onClose}
                    problem={problem}
                  />
                ))}
              </Box>
            </Stack>
          </>
        ) : (
          <Typography variant="f14Regular">{t('drawer.noData')}</Typography>
        )}
        <Stack spacing={1}>
          <Typography variant="f16Medium">{t('drawer.eligibilityRuleTitle')}</Typography>
          <Typography variant="f14Regular">{t('drawer.eligibilityRule')}</Typography>
        </Stack>
      </Box>
    </Drawer>
  );
};
